/**
 * The retention slice of the wire — Task U11 (Tier-3 live-tenant readiness),
 * consuming `GET`/`PUT /api/v1/admin/retention` (`src/modules/retention/routes.ts`,
 * `openapi.yaml`'s `RetentionPolicyList` / `RetentionPolicyUpdateRequest` /
 * `RetentionPolicyUpdateResult` schemas). Hand-written to mirror the contract, the
 * same discipline `permission-grants.ts` and `employees.ts` state: transcribe what
 * the screen reads, not the whole document.
 *
 * Both operations are `x-wp-permissions: [workforce.manage_retention]`,
 * `x-wp-scope: company` — the caller's active company, never a query parameter
 * (API_CONTRACT §7.4 rule 1). `GET` is unpaginated: the catalogue is a closed
 * vocabulary of eighteen classes, not a list that grows.
 *
 * ── The version is per class, and it is not `updated_at` ────────────────────
 *
 * There is no `ETag` header on this surface and no `If-Match` to send back. The
 * policy is one row per `(company_id, data_class)` and the screen edits many of them
 * at once, so the precondition rides **in the body, one `version` per class**. A
 * class sent without one is refused by the server; the screen never invents one, and
 * in particular never derives one from `updated_at` — two writes inside one
 * millisecond share a timestamp, which is the exact failure a precondition exists to
 * prevent (`src/modules/attendance/service.ts:463-467`).
 *
 * ── A `PUT` here is a partial accept, not a success or a failure ────────────
 *
 * `RetentionPolicyUpdateResult` carries `applied` **and** `rejected`, and both can
 * be non-empty in one response. `200` means at least one class landed; `409` means
 * none did and at least one was refused. Either way the body is the same shape, so
 * the screen renders the conflict beside the row it belongs to rather than as one
 * toast that cannot say which class failed.
 */

export const RETENTION_ADMIN_PATH = '/api/v1/admin/retention';

/**
 * `RetentionDataClass` of `openapi.yaml`, in its own declared order — the eighteen
 * values of the CHECK on `wp_retention_policies.data_class`.
 *
 * Spelled here rather than imported from anywhere: this bundle has no access to
 * `src/`, and the screen renders the classes in **the order the server sends them**
 * (alphabetical, `ORDER BY data_class`), so this array is only a type source and a
 * label key, never the render order.
 */
export const RETENTION_DATA_CLASSES = [
  'raw_activity',
  'heartbeat',
  'aggregate_hourly',
  'aggregate_daily',
  'derived_daily',
  'screenshot',
  'audit',
  'report',
  'alert',
  'incident',
  'ai_insight',
  'cache',
  'jobs',
  'short_ops',
  'recruiting_pii',
  'hr_records',
  'payroll',
  'geo_checkin',
] as const;

export type RetentionDataClass = (typeof RETENTION_DATA_CLASSES)[number];

/** Where a class's floor comes from — `RetentionPolicyClass.floor_source`. */
export type RetentionFloorSource = 'bounds' | 'record_keeping';

/**
 * `RetentionPolicyClass` of `openapi.yaml`. Every member the schema marks
 * `required` is written out, `T | null` where it is nullable, because the route
 * always sends the key.
 */
export interface RetentionPolicyClass {
  readonly data_class: RetentionDataClass;
  readonly retention_days: number;
  readonly enabled: boolean;
  readonly dry_run_default: boolean;
  /** The compare-and-set counter. Send it back on the class you change. */
  readonly version: number;
  readonly updated_at: string;
  readonly updated_by: string | null;
  /** The operational bracket `wp_ret_pol_bounds_ck` allows. */
  readonly min_days: number;
  readonly max_days: number;
  /**
   * The smallest value the **server** accepts. Never below `min_days`, sometimes
   * above it. The input's `min` is set from this so the UI does not offer a value
   * the server would refuse — a courtesy, not the enforcement.
   */
  readonly floor_days: number;
  readonly floor_source: RetentionFloorSource;
  readonly floor_citation: string | null;
  /** The class has no table to sweep yet. A declared absence, never a zero. */
  readonly awaiting_migration: boolean;
}

/** `RetentionPolicyList` — `{ classes }`, unpaginated, one row per data class. */
export interface RetentionPolicyList {
  readonly classes: readonly RetentionPolicyClass[];
}

/** One member of `RetentionPolicyUpdateRequest.classes`. */
export interface RetentionPolicyChangeRequest {
  readonly data_class: RetentionDataClass;
  /** Mandatory. A class named without one is a blind overwrite. */
  readonly version: number;
  readonly retention_days?: number;
  readonly enabled?: boolean;
  readonly dry_run_default?: boolean;
  /** Mandatory when `retention_days` is below the class's current window. */
  readonly acknowledge_data_loss?: boolean;
}

/** `RetentionPolicyUpdateRequest`. */
export interface RetentionPolicyUpdateRequest {
  readonly reason: string;
  readonly classes: readonly RetentionPolicyChangeRequest[];
}

/** One class that landed. `version` is what the next write must send back. */
export interface RetentionPolicyApplied {
  readonly data_class: RetentionDataClass;
  readonly previous_days: number;
  readonly retention_days: number;
  readonly shortened: boolean;
  readonly version: number;
  readonly updated_at: string;
}

/** One class that did not. Always `WORKFORCE_VERSION_CONFLICT`. */
export interface RetentionPolicyRejected {
  readonly data_class: RetentionDataClass;
  readonly code: 'WORKFORCE_VERSION_CONFLICT';
  readonly expected_version: number;
  readonly actual_version: number;
}

/**
 * `RetentionPolicyUpdateResult` — the body of both the `200` and the `409`.
 *
 * `classes` is the whole catalogue re-read after the batch, so the screen shows what
 * the database holds rather than what it hoped it would hold.
 */
export interface RetentionPolicyUpdateResult {
  readonly applied: readonly RetentionPolicyApplied[];
  readonly rejected: readonly RetentionPolicyRejected[];
  readonly classes: readonly RetentionPolicyClass[];
}

/** The minimum a written reason must be, mirroring `MIN_POLICY_REASON_LENGTH`. */
export const RETENTION_REASON_MIN_LENGTH = 10;
